import { t } from "i18next";
import moment from "moment";
import { MovieCardProps } from "../Components/Cards/MovieCard/movie-card";
import { cacheService, thumbnailService } from "../Core/Api/services";
import { ThumbnailSize } from "../Core/Api/Thumbnail/thumbnail-api-service";
import { AclTypes } from "../Core/Api/Types/acl-types";
import { CurrencyTypes } from "../Core/Api/Types/currency-types";
import { DbAssetObjectInfos } from "../Core/Api/Types/db-asset-object-infos";
import { DbCultureInfos } from "../Core/Api/Types/db-culture-infos";
import { DbMetadataFieldInfos } from "../Core/Api/Types/db-metadata-field-infos";
import { DbOrderBasketItemInfos } from "../Core/Api/Types/db-order-basket-item-infos";
import { DbOrderItemInfos } from "../Core/Api/Types/db-order-item-infos";
import { JobStatus } from "../Core/Api/Types/job-status";
import { Licences } from "../Core/Api/Types/licences";
import { ObjectClass } from "../Core/Api/Types/object-class";
import { ObjectLayer } from "../Core/Api/Types/object-layer";
import { ObjectStatus } from "../Core/Api/Types/object-status";
import { ObjectType } from "../Core/Api/Types/object-type";
import { OrderProductType } from "../Core/Api/Types/order-product-type";
import { OrderStatus } from "../Core/Api/Types/order-status";
import { PermissionType } from "../Core/Api/Types/permission-type";
import { PlatformMode } from "../Core/Api/Types/platform-mode";
import { ShareMediaType } from "../Core/Api/Types/share-media-type";
import { TranskodeVideoProfile } from "../Core/Api/Types/transkode-video-profile";
import { WorkflowStatus } from "../Core/Api/Types/workflow-status";
import { GetMetadataByTagName, IsPermissionGranted } from "../Core/Utils/asset-object-utils";
import GlobalConst from "../Core/Utils/Constants/globals";
import { routePaths } from "../Router/router";

export enum ConfigKeys {
  selfBroadcasted = "self-broadcasted",
  theatricalMaterial = "material",
  VODPackage = "vod",
  import = "import",
  transcode = "transcode",
  packageBundle = "package-bundle",
}

export const TimecodeToString = (timecode: number) => {
  // do not use
  const hour = Math.floor(timecode / 3600);
  const minutes = Math.floor((timecode % 3600) / 60);
  const seconds = Math.floor((timecode % 3600) % 60);

  const hourDisplay = hour > 9 ? hour : `0${hour}`;
  const minutesDisplay = minutes > 9 ? minutes : `0${minutes}`;
  const secondsDisplay = seconds > 9 ? seconds : `0${seconds}`;

  return `${hourDisplay}:${minutesDisplay}:${secondsDisplay}`;
};

export const StringToTimecode = (timecode: string) => {
  // do not use
  const [hours, minutes, seconds] = timecode.split(":").map((time) => parseInt(time));

  return seconds + minutes * 60 + hours * 60 * 60;
};

export const ComponentToHex = (c: number): string => {
  const hex = c.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
};

export const RgbToHex = (rgb: { r: number; g: number; b: number }): string => {
  return "#" + ComponentToHex(rgb.r) + ComponentToHex(rgb.g) + ComponentToHex(rgb.b);
};

export const AssetTypeHexColor = (assetType: ObjectType) => RgbToHex(AssetTypeRgbColor(assetType));

export const AssetTypeRgbColor = (assetType: ObjectType): { r: number; g: number; b: number } => {
  let rgbColor = { r: 51, g: 104, b: 2444 };

  switch (assetType) {
    case ObjectType.Video:
      rgbColor = { r: 51, g: 104, b: 244 };
      break;
    case ObjectType.Subtitle:
      rgbColor = { r: 31, g: 193, b: 154 };
      break;
    case ObjectType.ScribeSubtitleProject:
      rgbColor = { r: 145, g: 225, b: 122 };
      break;
    case ObjectType.Folder:
    case ObjectType.Group:
      rgbColor = { r: 247, g: 192, b: 53 };
      break;
    case ObjectType.Generic:
    case ObjectType.GroupMasterDcp:
    case ObjectType.GroupMasterImf:
    case ObjectType.Unknown:
      rgbColor = { r: 107, g: 107, b: 118 };
      break;
    case ObjectType.Audio:
    case ObjectType.AudioClip:
    case ObjectType.DolbyAtmosPackage:
      rgbColor = { r: 252, g: 164, b: 100 };
      break;
    case ObjectType.Image:
    case ObjectType.ImageSequence:
      rgbColor = { r: 220, g: 102, b: 250 };
      break;
    case ObjectType.DcpPackage:
    case ObjectType.ImfPackage:
      rgbColor = { r: 132, g: 66, b: 240 };
      break;
    case ObjectType.BundlePackage:
    case ObjectType.BundleComponent:
      rgbColor = { r: 166, g: 77, b: 255 };
      break;
    case ObjectType.GraphSkeleton:
      rgbColor = { r: 36, g: 240, b: 247 };
      break;
    case ObjectType.VirtualHead:
      rgbColor = { r: 132, g: 66, b: 240 };
      break;
  }
  return rgbColor;
};
export const AssetTypeAlphaColor = (assetType: ObjectType, value: number = 1) => {
  const rgbColor = AssetTypeRgbColor(assetType);

  return `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${value})`;
};

export const AssetLayerAlphaColor = (assetLayer: ObjectLayer, value: number = 1) => {
  let rgbColor = "51, 104, 2444";

  switch (assetLayer) {
    case ObjectLayer.Asset:
      rgbColor = "31, 193, 154";
      break;
    case ObjectLayer.Clip:
      rgbColor = "185, 185, 198";
      break;
    case ObjectLayer.Content:
      rgbColor = "252, 164, 100";
      break;
    case ObjectLayer.Group:
    case ObjectLayer.Head:
      rgbColor = "247, 192, 53";
      break;
    case ObjectLayer.SubContent:
      rgbColor = "166, 77, 255";
      break;
    case ObjectLayer.Title:
      rgbColor = "36, 240, 247";
      break;

    // TODO : map config and other types
    // case "config":
    //   rgbColor = "247, 192, 53";
    //   break;
  }

  return `rgba(${rgbColor}, ${value})`;
};

export const AssetVideoResolutionAlphaColor = (videoProfile: TranskodeVideoProfile, value: number = 1) => {
  let rgbColor = "51, 104, 2444";

  switch (videoProfile) {
    case TranskodeVideoProfile.SD:
      rgbColor = "185, 185, 198";

      break;
    case TranskodeVideoProfile.HD:
    case TranskodeVideoProfile.HDReady:
      //rgbColor = "31, 193, 154";
      rgbColor = "166, 77, 255";

      break;
    case TranskodeVideoProfile.FourK:
    case TranskodeVideoProfile.UHD:
      rgbColor = "252, 164, 100";
      break;

    // TODO : map config and other types
    // case "config":
    //   rgbColor = "247, 192, 53";
    //   break;
  }

  return `rgba(${rgbColor}, ${value})`;
};

export const AssetClassRgbColor = (classification: ObjectClass): { r: number; g: number; b: number } => {
  let rgbColor = { r: 51, g: 104, b: 2444 };

  switch (classification) {
    case ObjectClass.Raw:
      rgbColor = { r: 107, g: 107, b: 118 };

      break;
    case ObjectClass.Mezz:
    case ObjectClass.MezzAuto:
      //rgbColor = "31, 193, 154";
      rgbColor = { r: 132, g: 66, b: 240 };

      break;
    case ObjectClass.Dcp:
    case ObjectClass.Imf:
    case ObjectClass.Group:
      rgbColor = { r: 252, g: 164, b: 100 };
      break;
  }

  return rgbColor;
};

export const AssetClassHexColor = (assetClass: ObjectClass) => RgbToHex(AssetClassRgbColor(assetClass));

export const AssetClassAlphaColor = (classification: ObjectClass, value: number = 1) => {
  const rgbColor = AssetClassRgbColor(classification);

  return `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${value})`;
};

export const OrderTypeAlphaColor = (orderType: OrderProductType, value: number = 1) => {
  let rgbColor = "51, 104, 2444";
  // TODO : map other product types
  switch (orderType) {
    case OrderProductType.TranscodeByProfile:
      rgbColor = "247, 192, 53";
      break;
  }
  return `rgba(${rgbColor}, ${value})`;
};

// https://stackoverflow.com/questions/14446511/most-efficient-method-to-groupby-on-an-array-of-objects
export const GroupByToMap = <T, Q>(array: T[], predicate: (value: T, index: number, array: T[]) => Q) =>
  array.reduce((map, value, index, array) => {
    const key = predicate(value, index, array);
    map.get(key)?.push(value) ?? map.set(key, [value]);
    return map;
  }, new Map<Q, T[]>());

// Distinct generic method for array filter. Use as "myarray.filter(distinct)". https://codeburst.io/javascript-array-distinct-5edc93501dc4
export const Distinct = (value, index, self) => {
  return self.indexOf(value) === index;
};

export const DistinctByProp = (array: any[], propertyName: any) => {
  return array.filter((e, i) => array.findIndex((a) => a[propertyName] === e[propertyName]) === i);
};

export const IntersectBool = (array1: any[], array2: any[]): boolean => {
  if (!array1 || !array2) return true; // default true

  return array1.filter((value) => array2.includes(value)).length > 0;
};

export const IntersectArray = function (arr1: any[], arr2: any[]): any[] {
  var result = arr1.filter(function (n) {
    return arr2.indexOf(n) > -1;
  });

  return result;
};

export const LastIndex = function findLastIndex(array: any[], searchKey: any, searchValue: any) {
  var index = array
    .slice()
    .reverse()
    .findIndex((x) => x[searchKey] === searchValue);
  var count = array.length - 1;
  var finalIndex = index >= 0 ? count - index : index;
  return finalIndex;
};

export const GetPriorityFromInteger = (priority: number) => {
  if (priority > 0 && priority <= 25) return "low";
  else if (priority > 25 && priority <= 50) return "medium";
  else if (priority > 50 && priority <= 75) return "high";
  else return "urgent";
};

export const GetCurrencyChar = (currency: CurrencyTypes) => {
  switch (currency) {
    case CurrencyTypes.EUR:
      return "€";
    case CurrencyTypes.USD:
      return "$";
  }
};

export const GetOrderProductReadableType = (currency: OrderProductType) => {
  switch (currency) {
    case OrderProductType.AntipiracyInaSignature:
      return t("media:orderProductType.inaAntipiracy");
    case OrderProductType.AntipiracyYoutube:
      return t("menu:item.youtubeAntipiracy");
    case OrderProductType.CopyDelivery:
      return t("common:delivery");
    case OrderProductType.DcpReferenceFileSimple:
    case OrderProductType.DcpReferenceFileStitched:
      return t("media:orderProductType.dcpReferenceFile");
    case OrderProductType.FaspexSend:
      return t("media:orderProductType.deliveryWithAspera");
    case OrderProductType.ImageConverter:
      return t("media:orderProductType.imageConverter");
    case OrderProductType.InternalProxyTag:
      return t("media:orderProductType.shareProxyTagging");
    case OrderProductType.ManualTasks:
      return t("media:orderProductType.manualTask");
    case OrderProductType.Qualify:
      return t("common:qualify");
    case OrderProductType.QualityCheck:
      return t("menu:item.qualityCheckLowerCase");
    case OrderProductType.SubtitleConverter:
      return t("menu:item.subtitleConversion");
    case OrderProductType.SubtitleScribeProject:
      return t("media:orderProductType.subtitleProject");
    case OrderProductType.TranscodeBundleByProfile:
      return t("media:orderProductType.bundleMakerPackager");
    case OrderProductType.TranscodeByProfile:
      return t("media:orderProductType.selfBroadcastFileTranscoding");
    case OrderProductType.TranscodeComponentsLegacy:
      return t("media:orderProductType.vodPackageLegacy");
    case OrderProductType.TranscodeDownscaleAsMezz:
      return t("media:orderProductType.transcodeDownscaleAsMezzanine");
    case OrderProductType.TranscodeGeneric:
      return t("common:transcoding");
    case OrderProductType.TranscodeGenericSingleFile:
      return t("media:orderProductType.transcodingSingleFile");
    case OrderProductType.TranscodeImf:
      return t("media:orderProductType.imfTranscoding");
    case OrderProductType.TranscodeDcp:
      return t("media:orderProductType.dcpTranscoding");
    case OrderProductType.TranscodeNormalizer:
      return t("media:orderProductType.normalizerTranscoding");
    case OrderProductType.TranscodeSequence:
      return t("media:orderProductType.sequenceTranscoding");
    case OrderProductType.YoutubeChannelDelivery:
      return t("menu:item.youtubeChannelDelivery");
    case OrderProductType.DcpKdmGeneration:
      return t("media:orderProductType.dcpKdmGenerator");
    case OrderProductType.DcpTransferGlobecast:
      return t("media:orderProductType.dcpTransferByGlobecast");
  }

  return t("common:unknown");
};

export const GetAssetObjectReadableLayer = (objectType: ObjectLayer): string => {
  switch (objectType) {
    case ObjectLayer.Asset:
      return t("common:assetUpperCase");
    case ObjectLayer.Clip:
      return t("common:upperCaseClip");
    case ObjectLayer.Content:
      return t("common:content");
    case ObjectLayer.Group:
      return t("common:group");
    case ObjectLayer.Title:
      return t("common:title");
    case ObjectLayer.SubContent:
    case ObjectLayer.Head:
    default:
      return t("common:unknown");
  }
};

export const GetShareMediaType = (shareMediaType: ShareMediaType) => {
  switch (shareMediaType) {
    case ShareMediaType.Download:
      return t("common:download");
    case ShareMediaType.StreamingBasic:
      return t("media:shareMediaType.simpleStreamingFastMode");
    case ShareMediaType.StreamingTagSecure:
      return t("media:shareMediaType.securedStreamingWithTextInlay");
  }
};

export const GetAssetObjectReadableType = (objectType: ObjectType): string => {
  switch (objectType) {
    case ObjectType.Archive:
      return t("common:archive");
    case ObjectType.Audio:
      return t("common:audio");
    case ObjectType.AudioClip:
      return t("media:objectType.audioClip");
    case ObjectType.BundleComponent:
      return t("media:objectType.bundleComponent");
    case ObjectType.BundlePackage:
      return t("media:objectType.bundle");
    case ObjectType.DashPackage:
      return t("media:objectType.dash");
    case ObjectType.DcpPackage:
      return "DCP";
    case ObjectType.Document:
      return t("media:objectType.document");
    case ObjectType.DolbyAtmosData:
      return t("media:objectType.dolbyAtmosData");
    case ObjectType.DolbyAtmosPackage:
      return t("media:objectType.dolbyAtmosPackage");
    case ObjectType.Folder:
      return t("common:folder");
    case ObjectType.Font:
      return t("media:objectType.font");
    case ObjectType.Generic:
      return t("common:generic");
    case ObjectType.GraphSkeleton:
      return t("media:objectType.graphSkeleton");
    case ObjectType.Group:
      return t("common:group");
    case ObjectType.GroupMasterDcp:
      return t("media:objectType.dcpGroupMaster");
    case ObjectType.GroupMasterImf:
      return t("media:objectType.imfGroupMaster");
    case ObjectType.Image:
      return t("common:image");
    case ObjectType.ImageSequence:
      return t("menu:item.imageSequence");
    case ObjectType.ImfPackage:
      return "IMF";
    case ObjectType.MetadataDcpAssetMap:
      return "ASSETMAP";
    case ObjectType.MetadataDcpCpl:
      return "CPL";
    case ObjectType.MetadataDcpKdm:
      return "KDM / DKDM";
    case ObjectType.MetadataDcpPkl:
      return "PKL";
    case ObjectType.MetadataDcpVolIndex:
      return "VOLINDEX";
    case ObjectType.Metadata:
    case ObjectType.MetadataHdr10:
    case ObjectType.MetadataHdrDolbyVision:
    case ObjectType.MetadataImfAssetMap:
    case ObjectType.MetadataImfCpl:
    case ObjectType.MetadataImfKdm:
    case ObjectType.MetadataImfOpl:
    case ObjectType.MetadataImfPkl:
    case ObjectType.MetadataImfVolIndex:
    case ObjectType.MetadataProtoolsSession:
      return t("common:metadata");
    case ObjectType.Others:
      return t("common:other");
    case ObjectType.ScribeSubtitleProject:
      return t("menu:item.subtitlingProject");
    case ObjectType.Subtitle:
      return t("field:placeholder.subtitle");
    case ObjectType.TimetoolSequence:
      return t("media:objectType.timetoolSequence");
    case ObjectType.Video:
      return t("common:video");
    case ObjectType.VideoClip:
      return t("media:objectType.videoClip");
    case ObjectType.PhysicalItem:
      return t("media:objectType.physicalItem");
    case ObjectType.PhysicalBuilding:
      return t("common:building");
    case ObjectType.PhysicalPallet:
      return t("media:objectType.pallet");
    case ObjectType.PhysicalRack:
      return t("media:objectType.rack");
    case ObjectType.PhysicalRoom:
      return t("common:room");
    case ObjectType.PhysicalBox:
      return t("media:objectType.box");
    default:
      return objectType;
  }
};

export const GetOrderStatusLabel = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.ABORTED:
      return t("common:aborted");
    case OrderStatus.CANCELED:
      return t("common:canceled");
    case OrderStatus.COMPLETED:
      return t("common:delivered");
    case OrderStatus.CREATED:
      return t("common:created");
    case OrderStatus.FAILED:
      return t("common:fixing");
    case OrderStatus.MANUAL_DELIVERY_BEFORE_COMPLETED:
      return t("media:objectStatus.manualDeliveryBeforeCompleted");
    case OrderStatus.QUEUED:
      return t("common:pending");
    case OrderStatus.QUOTE_ACCEPTED:
      return t("common:quoteAccepted");
    case OrderStatus.QUOTE_DECLINED:
      return t("common:quoteDeclined");
    case OrderStatus.QUOTE_PROPOSED:
      return t("common:quoteProposed");
    case OrderStatus.QUOTE_REQUESTED:
      return t("common:quoteRequested");
    case OrderStatus.READY:
      return t("common:ready");
    case OrderStatus.RUNNING:
      return t("common:processing");
    case OrderStatus.STANDBY:
      return t("common:standby");
    case OrderStatus.UNKNOWN:
      return t("common:unknown");
    case OrderStatus.WAITING_APPROVAL:
      return t("page:pageFilter.subTitle.pendingApproval");
    case OrderStatus.SCHEDULED:
      return t("common:scheduled");
    default:
      return status;
  }
};

export const GetObjectStatusLabel = (status: ObjectStatus, isAdam?: boolean) => {
  switch (status) {
    case ObjectStatus.Accepted:
      return isAdam ? "PAV" : t("common:accepted");
    case ObjectStatus.Deleted:
      return t("common:deleted");
    case ObjectStatus.Disabled:
      return t("common:disabled");
    case ObjectStatus.EditingInProgress:
      return t("media:objectStatus.editingInProgress");
    case ObjectStatus.NeedCheckOrEditing:
      return isAdam ? "HAB" : t("media:objectStatus.needCheckOrEditing");
    case ObjectStatus.New:
      return t("common:pending");
    case ObjectStatus.Offline:
      return t("media:objectStatus.offline");
    case ObjectStatus.ParsedAndProbbed:
      return t("common:processing");
    case ObjectStatus.ProjectCompleted:
      return t("media:objectStatus.projectCompleted");
    case ObjectStatus.ProjectInProgress:
      return t("media:objectStatus.projectInProgress");
    case ObjectStatus.ProjectReady:
      return t("media:objectStatus.projectReady");
    case ObjectStatus.ProjectRejected:
      return t("media:objectStatus.projectRejected");
    case ObjectStatus.ProjectStandby:
      return t("media:objectStatus.projectStandby");
    case ObjectStatus.QualityCheckInProgress:
      return t("media:objectStatus.qualityCheckInProgress");
    case ObjectStatus.Ready:
      return t("common:ready");
    case ObjectStatus.Rejected:
      return t("common:rejected");
    case ObjectStatus.RejectedAuto:
      return t("media:objectStatus.rejectedAuto");
    case ObjectStatus.ToBeDigitalized:
      return t("media:objectStatus.toBeDigitalized");
    case ObjectStatus.ToBeNormalized:
      return t("media:objectStatus.toBeNormalized");
    case ObjectStatus.ToBeQCed:
      return t("common:pendingQc");
    case ObjectStatus.Undefined:
      return t("common:undefined");
    case ObjectStatus.Unmapped:
      return t("media:objectStatus.unmapped");
    case ObjectStatus.Unused:
      return t("media:objectStatus.unused");
    case ObjectStatus.WaitingForMEZZReference:
      return t("common:waitingForMezz");
    case ObjectStatus.MissingMetadata:
      return t("common:missingMetadata");
    case ObjectStatus.AcceptedForBroadcast:
      return isAdam ? "PAD" : status;
    case ObjectStatus.Delivered:
      return isAdam ? "LIVR" : status;
    default:
      return status;
  }
};

export const GetMainLanguageOfCountry = (country: DbCultureInfos) => {
  if (!country || !country.languages) return null;

  var lang = country.languages.find((x) => x.isMain);
  return lang !== undefined ? lang : country.languages.find((x) => true);
};

export const GetEnumValueLabelMap = (enumType: any, labelGetter?: (any) => string): any => {
  let res: any = [];
  Object.keys(enumType).map((x) => res.push({ value: x, label: labelGetter ? labelGetter(x) : x }));
  return res;
};

export const GetFileSize = (sizeInBytes: number): string => {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;

  while (sizeInBytes >= 1024) {
    // TODO : for asset id 458735 we have 968.000 instead of 968.250 (angular front) but we have 718.737 Ok
    sizeInBytes /= 1024.0;
    ++unitIndex;
  }

  return sizeInBytes.toFixed(2) + " " + units[unitIndex];
};

export const UserHasAcl = (aclType: AclTypes): boolean => {
  return UserHasAcls([aclType]);
};

export enum AclMatch {
  All,
  OneAtLeast,
}

export const UserHasAcls = (acls: AclTypes[], match: AclMatch = AclMatch.OneAtLeast): boolean => {
  const userAcls = cacheService.GetACL();
  return match === AclMatch.All
    ? acls.every((acl) => userAcls.includes(acl))
    : acls.filter((value) => userAcls.includes(value)).length > 0;
};

export const UserHasLicence = (licence: Licences): boolean => {
  return UserHasLicences([licence]);
};

export const UserHasLicences = (licences: Licences[]): boolean => {
  const userLicenses = cacheService.GetLicenses();
  return licences.every((licence) => userLicenses.includes(licence));
};

export const GetFormattedDate = (date?: Date | null, format?: string): string => {
  return moment(date).format(format || GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS);
};

export const GetFormattedDateTime = (date?: Date | null, format?: string): string => {
  return moment(date).format(format || GlobalConst.MOMENT_DATE_FORMAT_FULL);
};

export const OnlyInLeft = <T>(left: T[], right: T[], compareFunc: (leftValue: T, rightvalue: T) => boolean) =>
  left.filter((leftValue) => !right.some((rightValue) => compareFunc(leftValue, rightValue)));

export const GetFileNameFromPath = (path: string) => {
  return path.split("\\").pop()?.split("/").pop();
};

export const GetFileExtensionFromPath = (path: string) => {
  return path.split(".").pop()?.toLowerCase();
};

export const RandomIntFromInterval = (min: number, max: number) => {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const OpenLinkInNewTab = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export const GetTitleRouterPath = (isDriveMode: boolean = false) => {
  return cacheService.GetPlatformMode() === PlatformMode.Distribution && !isDriveMode
    ? routePaths.title.path
    : routePaths.asset.path;
};

export const IsEmail = (email: string): boolean => {
  let regex: RegExp =
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g;
  return regex.test(email.toLowerCase());
};

export const MakeUnique = (array: any[], keys: string[]) => {
  if (!keys.length || !array.length) return [];

  return array.reduce((list, item) => {
    const hasItem = list.find((listItem) => keys.every((key) => listItem[key] === item[key]));
    if (!hasItem) list.push(item);
    return list;
  }, []);
};

export const MoveInArray = (array: any[], fromIndex: number, toIndex: number) => {
  let element = array[fromIndex];
  array.splice(fromIndex, 1);
  array.splice(toIndex, 0, element);
};

export const HashCode = (str: string) => {
  var hash = 0;
  if (str !== null && str !== undefined)
    for (var i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return hash;
};

export const IntToArgb = (i: number) => {
  let hex =
    ((i >> 24) & 0xff).toString(16) +
    ((i >> 16) & 0xff).toString(16) +
    ((i >> 8) & 0xff).toString(16) +
    (i & 0xff).toString(16);
  hex += "000000";
  // Sometimes the string returned will be too short so we
  // add zeros to pad it out, which later get removed if
  // the length is greater than six.
  return hex.substring(0, 6);
};

export const TextToHexaColor = (text: string) => {
  return "#" + IntToArgb(HashCode(text));
};

export interface ConsecutiveRangesResult {
  in: number;
  out: number | null;
}

export const ConsecutiveRanges = (array: number[]): ConsecutiveRangesResult[] => {
  let length = 1;
  let list: ConsecutiveRangesResult[] = [];

  // If the array is empty,
  // return the list
  if (array.length === 0) return list;

  // Traverse the array from first position
  for (let i = 1; i <= array.length; i++) {
    // Check the difference between the
    // current and the previous elements
    // If the difference doesn't equal to 1
    // just increment the length variable.
    if (i === array.length || array[i] - array[i - 1] !== 1) {
      // If the range contains
      // only one element.
      // add it into the list.
      if (length === 1) {
        list.push({ in: array[i - length], out: null });
      } else {
        // Build the range between the first
        // element of the range and the
        // current previous element as the
        // last range.
        list.push({ in: array[i - length], out: array[i - 1] });
      }

      // After finding the first range
      // initialize the length by 1 to
      // build the next range.
      length = 1;
    } else length++;
  }

  return list;
};

// Extends string class
declare global {
  interface String {
    includesAllWords(pattern: string): boolean;
    isEmpty(): boolean;
  }
}

String.prototype.includesAllWords = function (pattern: string, checkSpecialChars: boolean = false): boolean {
  let words = checkSpecialChars
    ? pattern.toLowerCase().split(" ")
    : pattern
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .split(" ");
  let text = checkSpecialChars
    ? this.toLowerCase()
    : this.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

  return words.every((x) => text.includes(x));
};

String.prototype.isEmpty = function (): boolean {
  return this.length === 0 || !/\S/.test(this);
};

// Extends date class
declare global {
  interface Date {
    isValid(): boolean;
  }
}

Date.prototype.isValid = function () {
  // An invalid date object returns NaN for getTime() and NaN is the only
  // object not strictly equal to itself.
  return this.getTime() === this.getTime();
};

export function IsContentDeletable(object: DbAssetObjectInfos) {
  if (object.layer !== ObjectLayer.Content && object.layer !== ObjectLayer.Group) return false;

  const noLinkedAssets =
    object.childObjects === null || object.childObjects === undefined || object.childObjects.length === 0; // Check if there are no linked assets.
  const hasAcl = UserHasAcl(AclTypes.aclDeleteMetadataContentAssetsDeleted);
  let onlyDeletedLinkedAssets = false;
  if (object.childObjects !== null && object.childObjects !== undefined)
    onlyDeletedLinkedAssets = object.childObjects.filter((x) => x.isDeleted).length === object.childObjects.length;
  const isAlreadyDeleted = object.isDeleted;
  const userHasPerms = IsPermissionGranted(object, PermissionType.Trash);
  return (noLinkedAssets || (hasAcl && onlyDeletedLinkedAssets)) && !isAlreadyDeleted && userHasPerms;
}

export function IsStringAnId(query: string): boolean {
  let number: boolean = !isNaN(Number(query));
  let len = query.length;

  if (len < 5) number = false; // Considering we have object Ids up to 1M. Else we can't search "1995" movie title!

  return number;
}

export function FindDuplicates<T>(array: T[]): { value: T; count: number }[] {
  const duplicates: { [key: string]: number } = {};

  // Count the occurrences of each element
  array.forEach(function (element) {
    const stringifiedElement = JSON.stringify(element);
    duplicates[stringifiedElement] = (duplicates[stringifiedElement] || 0) + 1;
  });

  // Filter out non-duplicate elements
  const duplicateKeys = Object.keys(duplicates).filter(function (key) {
    return duplicates[key] > 1;
  });

  // Return duplicates and their counts
  return duplicateKeys.map(function (key) {
    return {
      value: JSON.parse(key),
      count: duplicates[key],
    };
  });
}

export function IsGuid(inputString) {
  const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return guidRegex.test(inputString);
}

export function containsOnlyDigits(input: string): boolean {
  // Regular expression to match only digits
  const digitRegex = /^[0-9]+$/;
  return digitRegex.test(input);
}

export function True(): boolean {
  return true;
}

export function GetRowValueFromStatus(status: any) {
  switch (status) {
    case JobStatus.READY:
    case JobStatus.CREATED:
    case WorkflowStatus.READY:
    case WorkflowStatus.CREATED:
    case ObjectStatus.New:
      return JobStatus.READY;
    case JobStatus.RUNNING:
    case WorkflowStatus.RUNNING:
    case ObjectStatus.ParsedAndProbbed:
    case ObjectStatus.QualityCheckInProgress:
      return JobStatus.RUNNING;
    case JobStatus.COMPLETED:
    case WorkflowStatus.COMPLETED:
    case ObjectStatus.Accepted:
      return JobStatus.COMPLETED;
    case JobStatus.ABORTED:
    case WorkflowStatus.ABORTED:
      return JobStatus.ABORTED;
    case JobStatus.CANCELLED:
    case WorkflowStatus.CANCELLED:
    case ObjectStatus.ToBeQCed:
    case ObjectStatus.ToBeDigitalized:
    case ObjectStatus.NeedCheckOrEditing:
    case ObjectStatus.WaitingForMEZZReference:
    case ObjectStatus.MissingMetadata:
      return JobStatus.CANCELLED;
    case JobStatus.FAILED:
    case WorkflowStatus.FAILED:
    case ObjectStatus.Rejected:
    case ObjectStatus.RejectedAuto:
      return JobStatus.FAILED;
  }
  return;
}

export function DownloadFile(data: string, type: string, fileName: string) {
  const blob = new Blob([data], { type: type });
  const url = window.URL.createObjectURL(blob);

  const linkElement = document.createElement("a");
  linkElement.href = url;
  linkElement.download = fileName;

  const clickEvent = new MouseEvent("click", { view: window, bubbles: true, cancelable: false });
  linkElement.dispatchEvent(clickEvent);

  window.URL.revokeObjectURL(url);
}

export function DownloadFileFromUrl(url: string, fileName: string) {
  const linkElement = document.createElement("a");
  linkElement.href = url;
  linkElement.download = fileName;

  document.body.appendChild(linkElement);

  const clickEvent = new MouseEvent("click", { view: window, bubbles: true, cancelable: false });
  linkElement.dispatchEvent(clickEvent);

  document.body.removeChild(linkElement);
}

export async function checkAndDownloadFile(
  url: string,
  fileName: string
): Promise<{ success: boolean; errorMessage: string }> {
  try {
    const response = await fetch(url, { method: "GET" });
    if (response.ok) {
      DownloadFileFromUrl(url, fileName);
    } else return { success: false, errorMessage: t("alert:error.downloadNotAllowed") };
  } catch (e) {
    return { success: false, errorMessage: t("alert:error.downloadException") };
  }
  return { success: true, errorMessage: "" };
}

export function FilterObjectsByTypes(allowedTypes: ObjectType[], objects: any[]) {
  const result: any[] = [];

  for (let i = 0; i < objects.length; i++) {
    if (
      allowedTypes.length > 0 &&
      (allowedTypes.find((x) => x === objects[i].type) || allowedTypes.find((x) => x === objects[i].objectType))
    )
      result.push(objects[i]);
    else if (allowedTypes.length === 0) {
      if (
        objects[i].type !== ObjectType.VideoClip &&
        objects[i].objectType !== ObjectType.VideoClip &&
        objects[i].type !== ObjectType.AudioClip &&
        objects[i].objectType !== ObjectType.AudioClip
      )
        result.push(objects[i]);
    }
  }

  return result;
}

export function ConvertSecondsToHHMMSS(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(remainingSeconds).padStart(2, "0");
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export const IsListOfEmails = (value: string): string | null => {
  const pasteDelimiters = [" ", ",", ";", "\n"];

  for (let i = 0; i < pasteDelimiters.length; i++) {
    if (value.includes(pasteDelimiters[i])) return pasteDelimiters[i];
  }

  return null;
};

export function isDbOrderBasketItemInfos(
  item: DbOrderItemInfos | DbOrderBasketItemInfos
): item is DbOrderBasketItemInfos {
  return "selectedProduct" in item;
}

export function CheckPassword(
  newPassword: string,
  repeatNewPassword: string
): { success: boolean; errorMessages: string[] } {
  // Trim
  newPassword = newPassword.trim();
  repeatNewPassword = repeatNewPassword.trim();

  let errorMessages: string[] = [];

  if (newPassword.length < 8) errorMessages.push(t("field:errorMessage.haveAtLeastCountCharacters", { count: 8 }));

  if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword))
    errorMessages.push(
      t("field:errorMessage.containAtLeaseLowerAndUpperCase", { minimumLowerCase: 1, minimumUpperCase: 1 })
    );

  let format = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
  if (!format.test(newPassword)) errorMessages.push(t("field:errorMessage.haveSpecialCharacter"));

  if (newPassword !== repeatNewPassword) errorMessages.push(t("field:errorMessage.newPasswordMustBeIdentical"));

  if (errorMessages.length === 0) return { success: true, errorMessages: [] };
  else return { success: false, errorMessages: errorMessages };
}

export function mapTitleObjectToMovieCard(title: DbAssetObjectInfos, metadataFields: DbMetadataFieldInfos[]) {
  let casts: string[] = GetMetadataByTagName(title, "casts", metadataFields)
    ?.value?.filter((x) => (x.function === "Actor" || x.function === "Acteur") && x.languageLocalizedCultureId === null)
    .map((act) => `${act.name} ${act.lastname}`);

  let productionDate = GetMetadataByTagName(title, "productionDate")?.value;

  const res: Omit<MovieCardProps, "index"> = {
    id: title.id || 0,
    //to: "#",
    cover: thumbnailService.GetObjectPosterUrl(title.id, ThumbnailSize.medium),
    title: title.name || "",
    labelList:
      new Date(productionDate ?? "").getFullYear() === new Date().getFullYear()
        ? ["NEW"]
        : productionDate
        ? [String(new Date(productionDate ?? "").getFullYear())]
        : [],
    transparentChipText: GetMetadataByTagName(title, "titleType", metadataFields)?.value,
    favorite: false,
    expand: {
      contentList: [],
      audioList: [],
      subtitleList: [],
      tagList: [],
      quality: [...new Set(casts)].join(", "),
    },
    selectMode: true,
  };

  return res;
}

export function getExpirationDaysFromString(dateString: string) {
  const date = new Date(dateString);
  return getExpirationDaysFromDate(date);
}

export function getExpirationDaysFromDate(date: Date) {
  return Math.floor((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
}

export function timeSpanToMinutes(timeSpan) {
  if (timeSpan == null) return 0;

  let days = 0;
  let hours = 0;
  let minutesPart = 0;
  let seconds = 0;

  if (timeSpan.includes(".")) {
    const [daysPart, timePart] = timeSpan.split(".");
    days = parseInt(daysPart);
    [hours, minutesPart, seconds] = timePart.split(":").map(Number);
  } else {
    [hours, minutesPart, seconds] = timeSpan.split(":").map(Number);
  }

  const totalMinutes = days * 24 * 60 + hours * 60 + minutesPart + seconds / 60;
  return totalMinutes;
}

export function minutesToTimeSpan(minutes) {
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const remainingMinutes = minutes % 60;
  const seconds = 0;

  return `${days}.${String(hours).padStart(2, "0")}:${String(remainingMinutes).padStart(2, "0")}:${String(
    seconds
  ).padStart(2, "0")}`;
}
